import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { APIProvider } from '@vis.gl/react-google-maps'
import { ThemeProvider as NextThemeProvider } from 'next-themes'
import './App.css'
import OpeningPage from './pages/OpeningPage'
import MapPage from './pages/Map'
import ManagementPage from "./pages/ManagementPage";
import DebugFirebasePage from './pages/DebugFirebase';
import SodaTrackBackground from '@/components/SodaTrackBackground';
import CreateUsernamePage from './pages/CreateUser';
import ProtectedRoute from './components/ProtectedRoute';
import { AnimationProvider } from './context/AnimationContext';
import { ThemeProvider, THEME_VALUES } from './context/ThemeContext';
import ThemeVisualizer from './pages/ThemeVisualizer'
import PermissionReminder from './components/auth/PermissionReminder';
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { checkAuthState, selectUserProfile, selectAuthLoaded } from './store/slices/authSlice';
import { initializeGlobalData, selectGlobalDataInitialized, selectGlobalDataLoading } from './store/slices/globalDataSlice';
import { initializeLocationsListener } from './store/slices/locationsSlice';
import { initializePublicMapsListener } from './store/slices/customMapsSlice';
import { TopNavBar } from '@/components/layout';
import { Unsubscribe } from 'firebase/auth';
import { initializeFromProfile } from './store/slices/editorSettingsSlice';
import DocumentationPage from './pages/DocumentationPage';
import DiaryPage from './pages/DiaryPage';
import DiaryEntryPage from './components/diary/DiaryEntry';
import SodaSearchPage from './pages/SodaSearchPage';
import { LoadingSpinner } from './components/ui/loading-spinner';

function App() {
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector(selectUserProfile);
  const authLoaded = useAppSelector(selectAuthLoaded);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);

  // Local state to manage overall app readiness for core data
  const [coreDataReady, setCoreDataReady] = useState(false);
  const [initialDispatchesDone, setInitialDispatchesDone] = useState(false);

  useEffect(() => {
    // Dispatch checkAuthState and store the unsubscribe function
    const promise = dispatch(checkAuthState());
    let unsubscribe: Unsubscribe | null = null;

    promise.then((action) => {
      // The payload of the fulfilled action is the unsubscribe function
      if (checkAuthState.fulfilled.match(action)) {
        unsubscribe = action.payload;
      }
    });

    // Cleanup function to unsubscribe when the App component unmounts
    return () => {
      if (unsubscribe) {
        console.log('[Auth] Unsubscribing from auth state changes.');
        unsubscribe();
      }
    };
  }, [dispatch]);

  // Initialize editor settings from user profile
  useEffect(() => {
    if (userProfile?.editorSettings && userProfile?.permissions) {
      // Only initialize for users with editor or admin permission
      if (userProfile.permissions.includes('editor') || userProfile.permissions.includes('admin')) {
        dispatch(initializeFromProfile({
          highlightStyle: userProfile.editorSettings.highlightStyle,
          highlightColor: userProfile.editorSettings.highlightColor,
          highlightsEnabled: userProfile.editorSettings.highlightsEnabled
        }));
      }
    }
  }, [dispatch, userProfile?.editorSettings, userProfile?.permissions]);

  // Initialize global data (brands, drinks, flavors, attributes)
  useEffect(() => {
    if (authLoaded && !initialDispatchesDone) {
      console.log('[App] Auth loaded. Dispatching initial data listeners (first time).');
      const userId = userProfile ? (userProfile as any).uid || null : null;
      dispatch(initializeGlobalData());
      dispatch(initializeLocationsListener());
      dispatch(initializePublicMapsListener(userId));
      setInitialDispatchesDone(true);
    }
  }, [dispatch, authLoaded, initialDispatchesDone, userProfile]);

  // Update coreDataReady state based on globalDataSlice
  useEffect(() => {
    if (globalDataInitialized && globalDataLoading !== 'pending') {
      setCoreDataReady(true);
    } else {
      setCoreDataReady(false); // Explicitly set to false if not ready
    }
  }, [globalDataInitialized, globalDataLoading]);

  // Show a global loading screen until authentication AND core data are ready
  if (!authLoaded || !coreDataReady) {
    return (
      <div className="flex items-center justify-center h-screen bg-background text-foreground">
        <LoadingSpinner />
        <span className="ml-4 text-xl">
          { !authLoaded ? "Initializing session..." : "Loading core application data..." }
        </span>
      </div>
    );
  }

  return (
    <NextThemeProvider 
      attribute="class" 
      defaultTheme="default"
      enableSystem
      themes={THEME_VALUES}
    >
      <ThemeProvider>
        <AnimationProvider>
          <APIProvider 
            apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} 
            onLoad={() => console.log('Maps API has loaded.')} 
            libraries={['places']}
            version="beta">
            <BrowserRouter>
              <div className="flex flex-col h-screen">
                <TopNavBar />
                <main className="flex-grow overflow-y-auto pt-16">
                  <SodaTrackBackground>
                    <Routes>
                      {/* Public routes - OpeningPage handles auth redirects internally */}
                      <Route path="/" element={<OpeningPage />} />
                      
                      {/* Create username page needs specific handling - review its logic */}
                      <Route path="/create-username" element={<CreateUsernamePage />} />
                      
                      {/* Protected routes - require auth, handled by ProtectedRoute */}
                      <Route element={<ProtectedRoute />}>
                        <Route path="/map" element={<MapPage />} />
                        <Route path="/manage" element={<ManagementPage />} />
                        <Route path="/debug" element={<DebugFirebasePage />} />
                        <Route path="/themes" element={<ThemeVisualizer />} />
                        <Route path="/documentation" element={<DocumentationPage />} />
                        <Route path="/diary" element={<DiaryPage />} />
                        <Route path="/diary/entry/:id" element={<DiaryEntryPage />} />
                        <Route path="/diary/entry/new" element={<DiaryEntryPage />} />
                        <Route path="/sodas" element={<SodaSearchPage />} />
                      </Route>
                      
                      {/* Fallback - catch all other routes and redirect to home */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    <PermissionReminder />
                  </SodaTrackBackground>
                </main>
              </div>
              <Toaster />
            </BrowserRouter>
          </APIProvider>
        </AnimationProvider>
      </ThemeProvider>
    </NextThemeProvider>
  )
}

export default App
